import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    FlatList,
    TouchableOpacity,
    StyleSheet,
    SafeAreaView,
    Platform,
    Modal,
    Linking,
    TextInput,
    ActivityIndicator
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import TopGreenHeader from './screens/Common/TopGreenHeader';
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import API_PATHS from './apiCalls/ApiPath';
import { pick, errorCodes, isErrorWithCode } from '@react-native-documents/picker';
import Toast from 'react-native-toast-message';

const DocumentsList = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    // Add Document states
    const [addDocModalVisible, setAddDocModalVisible] = useState(false);
    const [newDocName, setNewDocName] = useState('');
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [isUploading, setIsUploading] = useState(false);

    const navigation = useNavigation();

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const token = await AsyncStorage.getItem('token');
            const userId = await AsyncStorage.getItem('userId');

            const response = await axios.get(`${API_PATHS.BASE_URL}${API_PATHS.PROFILE_API}/${userId}`, {
                headers: {
                    'authorization': `Bearer ${token}`
                }
            });

            if (response.data && response.data.Success === 1 && response.data.Data?.response?.parent?.docs) {
                const docsObj = response.data.Data.response.parent.docs;
                const formattedDocs = docsObj.map((doc, idx) => ({
                    id: idx.toString(),
                    name: doc.name,
                    path: doc.path,
                    createdAt: doc.createdAt,
                    createdBy: doc.createdBy,
                }));
                setDocuments(formattedDocs);
            } else {
                setDocuments([]);
            }
        } catch (error) {
            console.error("Error fetching documents:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, []);

    const saveDocumentsToBackend = async (docsArray) => {
        const token = await AsyncStorage.getItem('token');
        const userId = await AsyncStorage.getItem('userId');

        const payloadDocs = docsArray.map(doc => ({
            name: doc?.name,
            path: doc?.path,
            createdBy: doc?.createdBy,
            createdAt: doc?.createdAt
        }));

        await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.EDIT_PARENT_DETAILS}`, {
            parentId: userId,
            docs: payloadDocs
        }, {
            headers: {
                'Content-Type': 'application/json',
                'authorization': `Bearer ${token}`
            }
        });
    };

    const handleDelete = (id) => {
        setDocToDelete(id);
        setModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (docToDelete) {
            setLoading(true);
            setModalVisible(false);
            try {
                const updatedDocs = documents.filter(doc => doc.id !== docToDelete);
                setDocuments(updatedDocs);
                await saveDocumentsToBackend(updatedDocs);
                await fetchDocuments();
            } catch (error) {
                console.error("Failed to delete document:", error);
                Toast.show({ type: 'error', text1: 'Failed to delete document on server.' });
                await fetchDocuments();
            } finally {
                setDocToDelete(null);
                setLoading(false);
            }
        } else {
            setModalVisible(false);
            setDocToDelete(null);
        }
    };

    const handleAddDocument = () => {
        setNewDocName('');
        setSelectedAsset(null);
        setAddDocModalVisible(true);
    };

    const handleDocumentSelection = async () => {
        try {
            const results = await pick({
                type: ['image/*', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
                mode: 'import',
            });
            const asset = results[0];
            setSelectedAsset(asset);
            setNewDocName(asset.name || '');
        } catch (err) {
            if (isErrorWithCode(err) && err.code === errorCodes.OPERATION_CANCELED) {
                return;
            }
            Toast.show({ type: 'error', text1: 'Could not pick file' });
        }
    };

    const handleUpload = async () => {
        if (!newDocName.trim() || !selectedAsset) {
            Toast.show({ type: 'info', text1: 'Please enter a name and select a document' });
            return;
        }

        setAddDocModalVisible(false);
        setIsUploading(true);

        try {
            const token = await AsyncStorage.getItem('token');

            const formData = new FormData();
            formData.append('file', {
                uri: selectedAsset.uri,
                name: selectedAsset.name || 'document',
                type: selectedAsset.type || 'application/octet-stream',
            });

            const response = await axios.post(`${API_PATHS.BASE_URL}${API_PATHS.UPLOAD_FILE}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'authorization': `Bearer ${token}`
                }
            });

            if (response.data && response.data.Success === 1 && response.data.Data?.url) {
                const newDoc = {
                    id: Date.now().toString(),
                    name: newDocName,
                    path: response.data.Data.url,
                    createdAt: Math.floor(Date.now() / 1000),
                    createdBy: "self"
                };
                await saveDocumentsToBackend([...documents, newDoc]);
            } else if (response.data && response.data.Data?.Location) {
                const newDoc = {
                    id: Date.now().toString(),
                    name: newDocName,
                    path: response.data.Data.Location
                };
                await saveDocumentsToBackend([...documents, newDoc]);
            }

            await fetchDocuments();
        } catch (error) {
            console.error("Upload error:", error);
            Toast.show({ type: 'error', text1: 'File upload failed!' });
        } finally {
            setIsUploading(false);
            setSelectedAsset(null);
        }
    };

    const renderItem = ({ item }) => {
        let formattedDate = "";
        if (item.createdAt) {
            const date = new Date(item.createdAt * 1000);
            const mo = ('0' + (date.getMonth() + 1)).slice(-2);
            const da = ('0' + date.getDate()).slice(-2);
            const yr = date.getFullYear();
            let hr = date.getHours();
            const min = ('0' + date.getMinutes()).slice(-2);
            const ampm = hr >= 12 ? 'PM' : 'AM';
            hr = hr % 12;
            hr = hr ? hr : 12;
            const hrStr = ('0' + hr).slice(-2);
            formattedDate = `${mo}/${da}/${yr} ${hrStr}:${min} ${ampm}`;
        }

        return (
            <View style={styles.itemContainer}>
                <TouchableOpacity
                    style={styles.addressInfo}
                    onPress={() => Linking.openURL(item.path)}
                >
                    <Text style={styles.tagText}>{item.name}</Text>
                    <Text style={{ color: '#908f96', fontSize: 12, marginTop: 4 }}>
                        {formattedDate ? `${formattedDate}, ` : 'Unknown, '}{item.createdBy == "self" ? "added by you" : item.createdBy == "admin" ? "added by admin" : "added by unknown"}
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={styles.deleteButton}
                    onPress={() => handleDelete(item.id)}
                >
                    <Text style={styles.deleteButtonText}>Delete</Text>
                </TouchableOpacity>
            </View>
        );
    };

    return (
        <SafeAreaView style={styles.container}>
            <TopGreenHeader
                Heading="Documents"
                navigation={navigation}
                isBackIcon={true}
                isNotification={false}
            />
            {loading ? (
                <View style={styles.loaderContainer}>
                    <ActivityIndicator size="large" color="#A8CF45" />
                </View>
            ) : (
                <FlatList
                    data={documents}
                    keyExtractor={(item) => item.id}
                    renderItem={renderItem}
                    ItemSeparatorComponent={() => <View style={styles.separator} />}
                    contentContainerStyle={styles.listContent}
                    ListEmptyComponent={<Text style={styles.emptyText}>No documents found.</Text>}
                />
            )}

            <View style={styles.bottomContainer}>
                <TouchableOpacity
                    style={[styles.addButton, isUploading && { backgroundColor: '#ccc' }]}
                    onPress={handleAddDocument}
                    disabled={isUploading}
                >
                    {isUploading ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <ActivityIndicator size="small" color="#fff" style={{ marginRight: 10 }} />
                            <Text style={styles.addButtonText}>Uploading...</Text>
                        </View>
                    ) : (
                        <Text style={styles.addButtonText}>Add Document</Text>
                    )}
                </TouchableOpacity>
            </View>

            {/* Delete Confirmation Modal */}
            <Modal
                animationType="slide"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={styles.modalContainer}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalText}>Are you sure you want to delete this document?</Text>
                        <View style={styles.modalButtons}>
                            <TouchableOpacity style={styles.button} onPress={handleConfirmDelete}>
                                <Text style={styles.buttonText}>Yes</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={[styles.button, { backgroundColor: '#ccc' }]} onPress={() => setModalVisible(false)}>
                                <Text style={styles.buttonText}>No</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>

            {/* Add Document Modal */}
            <Modal
                animationType="slide"
                transparent={true}
                visible={addDocModalVisible}
                onRequestClose={() => setAddDocModalVisible(false)}
            >
                <View style={styles.modalContainer}>
                    <View style={styles.addDocModalContent}>
                        <Text style={styles.modalTitle}>Add New Document</Text>

                        <TextInput
                            style={styles.input}
                            placeholder="Document Name"
                            placeholderTextColor="#999"
                            value={newDocName}
                            onChangeText={setNewDocName}
                        />

                        <TouchableOpacity
                            style={styles.addBox}
                            onPress={handleDocumentSelection}
                        >
                            {selectedAsset ? (
                                <Text style={[styles.addBoxText, { color: '#A6CE39', fontWeight: 'bold', textAlign: 'center', paddingHorizontal: 10 }]}>
                                    {selectedAsset.name || 'Document Selected'} ✓
                                </Text>
                            ) : (
                                <>
                                    <Text style={styles.plusIcon}>+</Text>
                                    <Text style={styles.addBoxText}>Tap to add document</Text>
                                </>
                            )}
                        </TouchableOpacity>

                        <View style={styles.modalButtons}>
                            <TouchableOpacity style={styles.button} onPress={handleUpload}>
                                <Text style={styles.buttonText}>Upload</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.button, { backgroundColor: '#ccc' }]}
                                onPress={() => setAddDocModalVisible(false)}
                            >
                                <Text style={styles.buttonText}>Cancel</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#fff',
    },
    loaderContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    listContent: {
        padding: 15,
        paddingBottom: 100, // accommodate for the bottom button container
    },
    itemContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 15,
    },
    addressInfo: {
        flex: 1,
        paddingRight: 15,
    },
    tagText: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#333',
        marginBottom: 5,
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        textTransform: 'capitalize',
    },
    addressText: {
        fontSize: 14,
        color: '#666',
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        lineHeight: 20,
    },
    deleteButton: {
        backgroundColor: '#ff4d4f',
        paddingHorizontal: 15,
        paddingVertical: 8,
        borderRadius: 5,
        justifyContent: 'center',
        alignItems: 'center',
    },
    deleteButtonText: {
        color: '#fff',
        fontWeight: 'bold',
        fontSize: 14,
    },
    separator: {
        height: 1,
        backgroundColor: '#eee',
    },
    bottomContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#fff',
        padding: 15,
        borderTopWidth: 1,
        borderColor: '#eee',
    },
    addButton: {
        backgroundColor: '#A8CF45', // Green color
        padding: 15,
        borderRadius: 10,
        alignItems: 'center',
    },
    addButtonText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: 'bold',
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    },
    emptyText: {
        textAlign: 'center',
        color: '#999',
        marginTop: 50,
        fontSize: 16,
    },
    modalContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    modalContent: {
        width: 300,
        padding: 20,
        backgroundColor: '#fff',
        borderRadius: 10,
        alignItems: 'center',
    },
    addDocModalContent: {
        width: 320,
        padding: 25,
        backgroundColor: '#fff',
        borderRadius: 10,
        alignItems: 'center',
    },
    modalText: {
        fontSize: 18,
        marginBottom: 20,
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        fontStyle: 'normal',
        color: '#000',
        textAlign: 'center',
    },
    modalTitle: {
        fontSize: 20,
        marginBottom: 20,
        color: '#333',
        fontFamily: Platform.OS === 'android' ? 'FacebookSansBold' : 'Facebook Sans',
        textAlign: 'center',
    },
    input: {
        width: '100%',
        borderColor: '#ddd',
        borderWidth: 1,
        borderRadius: 10,
        paddingHorizontal: 15,
        paddingVertical: 12,
        fontSize: 16,
        marginBottom: 20,
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        backgroundColor: '#f9f9f9',
    },
    addBox: {
        width: '100%',
        height: 120,
        borderColor: '#A8CF45',
        borderWidth: 2,
        borderStyle: 'dashed',
        borderRadius: 10,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 25,
        backgroundColor: '#fafafa',
    },
    plusIcon: {
        fontSize: 40,
        color: '#A8CF45',
        marginBottom: 5,
        fontWeight: '300',
    },
    addBoxText: {
        fontSize: 16,
        color: '#666',
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
    },
    modalButtons: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
    },
    button: {
        flex: 1,
        padding: 12,
        marginHorizontal: 10,
        backgroundColor: '#A6CE39',
        alignItems: 'center',
        borderRadius: 8,
    },
    buttonText: {
        color: '#fff',
        fontSize: 16,
        fontFamily: Platform.OS === 'android' ? 'FacebookSansRegular' : 'Facebook Sans',
        fontStyle: 'normal',
        fontWeight: 'bold',
    }
});

export default DocumentsList;
